import { Box, Chip, Divider, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import { CalendarToday, Schedule } from '@material-ui/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { SettlementsInvoicesKPIsResponseModel } from '../../../../../api/Argentia/endpoints/settlements/model/settlements'
import { EnumTipoCicloFacturacion } from '../../../../../api/oData'
import { envConfig } from '../../../../../config/envConfig'
import { formatDate } from '../../../../../helpers/formatDateHelper'
import { capKpiValue } from '../../../../../helpers/numberHelper'
import { CycleExecutionModel } from '../../../../invoiceCycles/invoiceCycles.model'
import { SettlementDetailKpiItem } from '../../../components/settlementDetailKpiItem/settlementDetailKpiItem'
import { SettlementKpiItem } from '../../../components/settlementKpiItem/settlementKpiItem'
import { SettlementInvoiceKpiType } from '../settlementInvoicesKpiRightContent/settlementInvoicesKpiRightContent'

interface SettlementKpiLeftContentProps {
  kpis?: SettlementsInvoicesKPIsResponseModel
  isLoading: boolean
  invoiceCycleType?: EnumTipoCicloFacturacion
  invoiceCycleDescription?: string
  executionData?: CycleExecutionModel | null
  isFetchingExecution?: boolean
  activeKpi?: SettlementInvoiceKpiType | null
  onKpiClick?: (kpiType: SettlementInvoiceKpiType) => void
}

export const SettlementsInvoicesKpiLeftContent: React.FC<SettlementKpiLeftContentProps> = ({
  kpis,
  isLoading,
  invoiceCycleType,
  invoiceCycleDescription,
  executionData,
  isFetchingExecution = false,
  activeKpi,
  onKpiClick
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'settlementsInvoices'])
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const getDetailText = () => {
    if (!executionData || !invoiceCycleType) {
      return null
    }

    const executionDate = executionData.executionDate
      ? formatDate(executionData.executionDate, 'DD/MM/YYYY - HH:mm:ss')
      : '--/--/---- - --:--:--'

    const cycleType = t(
      `enums:EnumTipoCicloFacturacion.${EnumTipoCicloFacturacion[invoiceCycleType]}`
    )

    const startDate = executionData.startDate
      ? formatDate(executionData.startDate, 'DD/MM/YYYY')
      : '--/--/----'

    const endDate = executionData.endDate
      ? formatDate(executionData.endDate, 'DD/MM/YYYY')
      : '--/--/----'

    return (
      <Box>
        <Box display="flex" alignItems="center" mb={1}>
          <Schedule fontSize="small" style={{ marginRight: 8, color: '#757575' }} />
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {t(
              'settlementsInvoices:components.kpiCard.kpiItems.totalInvoicesGenerated.executionLabel'
            )}
            : <span style={{ fontWeight: 400 }}>{executionDate}</span>
          </Typography>
        </Box>
        <Divider style={{ margin: '8px 0' }} />
        <Box display="flex" alignItems="center" mb={1}>
          <Typography variant="body2" style={{ fontWeight: 500, marginRight: 8 }}>
            {t(
              'settlementsInvoices:components.kpiCard.kpiItems.totalInvoicesGenerated.cycleTypeLabel'
            )}
            :
          </Typography>
          <Chip
            label={cycleType}
            size="small"
            color="primary"
            variant="outlined"
            style={{ fontWeight: 500 }}
          />
        </Box>
        <Divider style={{ margin: '8px 0' }} />
        <Box display="flex" alignItems="center">
          <CalendarToday fontSize="small" style={{ marginRight: 8, color: '#757575' }} />
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {t(
              'settlementsInvoices:components.kpiCard.kpiItems.totalInvoicesGenerated.periodLabel'
            )}
            :{' '}
            <span style={{ fontWeight: 400 }}>
              {startDate} - {endDate}
            </span>
          </Typography>
        </Box>
      </Box>
    )
  }

  const detailContent = getDetailText()

  return (
    <Box style={{ height: '100%' }} display="flex" flexDirection="column">
      <Box
        style={{ height: '100%', columnGap: 8 }}
        display="flex"
        flexDirection={isSmallScreen ? 'column' : 'row'}
        justifyContent={isSmallScreen ? 'space-evenly' : 'center'}
        alignItems="stretch"
        flexGrow={1}
      >
        <Box
          style={{
            flex: isSmallScreen ? undefined : '0 0 auto',
            display: 'flex',
            height: '100%',
            width: isSmallScreen ? '100%' : 'auto'
          }}
        >
          <SettlementKpiItem
            id="totalInvoicesGenerated"
            isLoading={isLoading}
            title={t(
              'settlementsInvoices:components.kpiCard.kpiItems.totalInvoicesGenerated.title'
            )}
            value={capKpiValue(kpis?.totalInvoicesGenerated, envConfig.kpiConfig.maxDisplayValue)}
            description={t(
              'settlementsInvoices:components.kpiCard.kpiItems.totalInvoicesGenerated.description'
            )}
            onClick={onKpiClick ? () => onKpiClick('totalInvoicesGenerated') : undefined}
            isActive={activeKpi === 'totalInvoicesGenerated'}
          />
        </Box>
        <Box
          style={{
            flex: isSmallScreen ? undefined : 1,
            display: 'flex',
            height: '100%',
            width: isSmallScreen ? '100%' : 'auto'
          }}
        >
          <SettlementDetailKpiItem isLoading={isFetchingExecution} detailContent={detailContent} />
        </Box>
      </Box>
    </Box>
  )
}
